//import 'tippy.js/dist/tippy.css';
import { getLocalizedProperty, uuid } from '@loyalty/shared-common';
import { renderToString } from 'react-dom/server';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericComponent } from '../../components-tree';
import { ReactBootstrap } from '../../ReactBootstrap';
import { ReferralDataSkeleton } from './ReferralDataSkeleton';
import { ReferralTypeEnum } from '../../utils/naming';
import { TranslationProvider } from '../TranslationProvider/TranslationProvider';
import { WidgetProps } from '../render-component';
import { showTooltip } from '../../utils/show-tooltip';

const CLASSNAME = 'qlfeditor-referral-code';

export interface ReferralDataAttributes {
  referralType: ReferralTypeEnum;
  showOnlyButton: boolean;
}

type ReferralDataComponent = GenericComponent<ReferralDataAttributes>;

type ReferralDataBackendProps = WidgetProps<ReferralDataComponent>;

interface ReferralDataFrontendProps {
  id: string;
  attributes: ReferralDataComponent['attributes'];
  referralData: string;
  referralDataClass: string;
  referralType: ReferralTypeEnum;
}

export function serializeReferallComponentProps({
  state,
  options,
  context,
}: ReferralDataBackendProps): ReferralDataFrontendProps {
  const { locale, t } = context.i18n;
  const referralType = options.attributes.referralType;

  const {
    profile: { identities },
    referralConfig,
    programId,
    programUrl,
  } = state;

  const referralCodeIdentity = identities?.find(
    identity => identity.kind === 'referralCode' && identity.programId === programId,
  );

  let referralData = '';
  let referralDataClass = CLASSNAME;

  if (referralCodeIdentity) {
    switch (referralType) {
      case ReferralTypeEnum.E_LINK:
        referralData =
          getLocalizedProperty(referralConfig, 'link', locale)?.replace(
            '{referralCode}',
            referralCodeIdentity.value?.toLowerCase(),
          ) || '';
        referralDataClass = `${CLASSNAME}__link`;
        break;
      case ReferralTypeEnum.P_LINK:
        referralData = `${programUrl}?referralCode=${referralCodeIdentity.value?.toLowerCase()}`;
        referralDataClass = `${CLASSNAME}__link`;
        break;
      default:
        referralData = referralCodeIdentity.value?.toLowerCase();
        referralDataClass = `${CLASSNAME}__code`;
    }
  }

  return {
    attributes: options.attributes,
    id: options.id || options.attributes.id || uuid(),
    referralType,
    referralData,
    referralDataClass,
  };
}

export function ReferralDataComponent(minProps: ReferralDataFrontendProps) {
  return (
    <TranslationProvider loader={<ReferralDataSkeleton />}>
      <ReferralData {...minProps} />
    </TranslationProvider>
  );
}

export function ReferralData(minProps: ReferralDataFrontendProps) {
  const { t } = useTranslation();
  const { id, attributes, referralData, referralDataClass, referralType } = minProps;
  const [srAnnouncement, setSrAnnouncement] = useState('');

  const { class: className, showOnlyButton } = attributes;

  const buttonLabel =
    referralType === ReferralTypeEnum.CODE
      ? t('editor.referral.copy.code')
      : t('editor.referral.copy.link');

  const handleCopyClick = (event: React.MouseEvent) => {
    navigator.clipboard.writeText(referralData).then(() => {
      showTooltip(event.target as any, t('generic.copied'));

      // Set screen reader announcement
      const announcement =
        referralType === ReferralTypeEnum.CODE
          ? t('aria.referral.copy.code')
          : t('aria.referral.copy.link');

      setSrAnnouncement(announcement);

      // Clear announcement after a short delay
      setTimeout(() => setSrAnnouncement(''), 2000);
    });
  };

  return (
    <div className={`${CLASSNAME} ${className || ''}`}>
      {showOnlyButton ? (
        ''
      ) : (
        <div
          id={`referral-data_${id}`}
          className={referralDataClass}
          aria-label={
            referralType === ReferralTypeEnum.CODE
              ? t('aria.referral.code')
              : t('aria.referral.link')
          }
        >
          {referralData}
        </div>
      )}
      <div className={`${CLASSNAME}__container`}>
        <button
          id={`referral-data_copy-button_${id}`}
          className={`${CLASSNAME}__button`}
          onClick={handleCopyClick}
        >
          {buttonLabel}
        </button>
        <div aria-live="polite" className="sr-only">
          {srAnnouncement}
        </div>
      </div>
    </div>
  );
}

export function ReferralDataRenderer(props: ReferralDataBackendProps, document?: Document): string {
  const reactProps = serializeReferallComponentProps(props);
  return renderToString(
    <ReactBootstrap
      component="ReferralDataComponent"
      props={reactProps}
      document={document}
      componentType="referral-data"
    />,
  );
}
